/**
 * Die öffentliche Anfrageseite, ohne Oberfläche: was der Server über einen Betrieb
 * herausgibt, wie daraus eine Preisschätzung wird, und woher die Vorschau `/e/demo`
 * ihre Daten nimmt.
 *
 * Hier steht nichts, was ein Interessent nicht erfahren dürfte. Was `public_enquiry_config`
 * liefert, ist der Tarif — Einheiten, Preisliste, verpflichtende Nebenkosten, Ortstaxe,
 * Kinderermäßigung, Anzahlungsregel. Keine Buchungen, keine Namen, keine Notizen.
 *
 * Gerechnet wird mit `priceStay()`, derselben Funktion, die später das Angebot erstellt.
 * Eine eigene, „einfachere" Rechnung für die Anfrageseite wäre eine zweite Wahrheit —
 * und der Gast würde genau dann anrufen, wenn beide Zahlen auseinandergehen.
 */

import { buildDemoDatabase, DEMO_TOURIST_TAX } from '../data/demoSeed'
import type { Database, Fee, ISODate, RatePeriod, Unit, VatScheme } from '../data/model'
import { addDays, todayISO } from './dates'
import {
  prepaymentFor, prepaymentPolicyOf, priceStay, resolveNightlyRate,
  type ChildRate, type ChildRateScope, type PriceLine, type TouristTaxRule,
} from './pricing'

export type Lang = 'de' | 'en'

/** Der Slug, unter dem die Vorschau liegt. Kein Betrieb darf ihn führen (Migration 0048). */
export const DEMO_SLUG = 'demo'

export interface EnquiryUnit {
  id: string
  name: string
  kind: string
  category?: string | null
  category_id?: string | null
  max_occupancy: number
  min_nights: number
  bedrooms: number | null
  size_sqm: number | null
  description: Record<string, string>
  amenities?: string[]
  base_price?: number
  base_occupancy?: number
  extra_person_price?: number
}

/** Nur die Felder der Preisliste, die `priceStay` braucht — ohne Saisonnamen. */
export type EnquiryRate = Pick<RatePeriod,
  'id' | 'unit_id' | 'category_id' | 'valid_from' | 'valid_to' | 'dow_mask'
  | 'price_per_night' | 'extra_person_price' | 'base_occupancy' | 'min_nights' | 'priority'>

export type EnquiryFee = Pick<Fee,
  'id' | 'unit_id' | 'code' | 'name' | 'basis' | 'amount' | 'vat_rate'
  | 'max_nights' | 'sort_order'>

export interface EnquiryPricing {
  vat_scheme: VatScheme
  vat_rate_accommodation: number
  prices_include_vat: boolean
  child_rates: ChildRate[]
  child_rate_scope: ChildRateScope
  prepayment: { mode: 'percent' | 'fixed' | 'none'; percent: number; fixed: number }
  rates: EnquiryRate[]
  /** Nur die verpflichtenden. Optionale schuldet der Gast erst, wenn er sie wählt. */
  fees: EnquiryFee[]
  tourist_tax: TouristTaxRule | null
}

export interface EnquiryConfig {
  slug: string
  name: string
  country: string
  currency: string
  locale: string
  accent: string | null
  intro: Record<string, string>
  lead_days: number
  max_nights: number | null
  default_open: boolean
  units: EnquiryUnit[]
  /** Fehlt bei einem Server, der Migration 0048 noch nicht hat — dann ohne Schätzung. */
  pricing?: EnquiryPricing | null
}

/**
 * Ob das Alter der Kinder überhaupt etwas ändert.
 *
 * Nur dann wird danach gefragt. Ein Betrieb ohne Kinderermäßigung und ohne
 * Altersbefreiung bei der Ortstaxe rechnet mit dem Alter dasselbe wie ohne — die Frage
 * wäre reine Tipparbeit auf einem Telefon, und zwar für eine Familie, also für die,
 * die es am wenigsten übrig hat.
 */
export function agesMatter(pricing: EnquiryPricing | null | undefined): boolean {
  if (!pricing) return false
  return pricing.child_rates.length > 0
    || pricing.tourist_tax?.exempt_under_age != null
}

export interface EstimateLine {
  label: string
  gross: number
}

export interface StayEstimate {
  nights: number
  gross: number
  /** Vorgeschlagene Anzahlung nach der Regel des Betriebs; 0 = keine. */
  prepayment: number
  lines: EstimateLine[]
  /**
   * Kinder sind angegeben, ihr Alter nicht — obwohl es den Preis senken würde. Die
   * Schätzung ist dann eine Obergrenze, und die Seite muss das dazusagen.
   */
  agesMissing: boolean
}

/**
 * Die Preis-Engine will einen vollständigen Datensatz; die Anfrageseite kennt nur den
 * öffentlichen Ausschnitt davon. Die fehlenden Felder sind für die Rechnung ohne
 * Bedeutung (Mandant, Objekt, Farbe) und werden hier aufgefüllt, statt sie durch die
 * Konfigurationsfunktion mitzuschleppen — herausgegeben wird nur, was gebraucht wird.
 */
function toUnit(u: EnquiryUnit): Unit | null {
  if (u.base_price == null || u.base_occupancy == null) return null
  return {
    id: u.id, tenant_id: '', property_id: '', name: u.name,
    kind: (u.kind as Unit['kind']) ?? 'other',
    max_occupancy: u.max_occupancy,
    base_occupancy: u.base_occupancy,
    base_price: u.base_price,
    extra_person_price: u.extra_person_price ?? 0,
    min_nights: u.min_nights,
    category_id: u.category_id ?? null,
    color: '', description: u.description, amenities: u.amenities ?? [],
    sort_order: 0, active: true,
  }
}

function toRates(p: EnquiryPricing): RatePeriod[] {
  return p.rates.map((r) => ({ ...r, tenant_id: '', name: '', active: true }))
}

function toFees(p: EnquiryPricing): Fee[] {
  return p.fees.map((f) => ({
    ...f, tenant_id: '', mandatory: true, taxable_age_from: null, active: true,
  }))
}

/** Die Beschriftung einer Zeile, wie der Gast sie lesen soll. */
function lineLabel(line: PriceLine, lang: Lang): string {
  const de = lang === 'de'
  if (line.nameJson) return line.nameJson[lang] ?? line.nameJson.de ?? line.code
  switch (line.code) {
    case 'accommodation': return de ? 'Übernachtung' : 'Accommodation'
    case 'extra_person': return de ? 'Zusatzperson' : 'Extra guest'
    case 'extra_person_child': return de ? 'Kind (Aufpreis)' : 'Child (surcharge)'
    case 'child_discount': return de ? 'Kinderermäßigung' : 'Child discount'
    default: return line.code
  }
}

/**
 * Der geschätzte Gesamtpreis für einen Aufenthalt — oder `null`, wenn sich keiner
 * begründen lässt.
 *
 * `null` ist eine Antwort und kein Fehler: ohne Preisliste, ohne hinterlegten Preis
 * oder bei ungültigem Zeitraum steht auf der Seite lieber gar keine Zahl als eine
 * erfundene. „€ 0,00 für sieben Nächte" ist schlimmer als Schweigen.
 */
export function estimateStay(args: {
  config: EnquiryConfig
  unit: EnquiryUnit
  arrival: ISODate
  departure: ISODate
  adults: number
  children: number
  childrenAges: number[]
  lang: Lang
}): StayEstimate | null {
  const { config, unit, arrival, departure, adults, children, childrenAges, lang } = args
  const pricing = config.pricing
  if (!pricing) return null
  if (!arrival || !departure || departure <= arrival) return null

  const engineUnit = toUnit(unit)
  if (!engineUnit) return null

  // Nur vollständige Altersangaben gehen in die Rechnung. Zwei Alter bei drei Kindern
  // ergäben eine Ermäßigung, die niemand versprochen hat — die Engine rechnet dann
  // ohnehin voll, und das soll sie auch.
  const agesComplete = children > 0 && childrenAges.length === children
    && childrenAges.every((a) => Number.isFinite(a) && a >= 0)

  const priced = priceStay({
    unit: engineUnit,
    ratePeriods: toRates(pricing),
    fees: toFees(pricing),
    touristTax: pricing.tourist_tax,
    arrival, departure, adults, children,
    childrenAges: agesComplete ? childrenAges : [],
    vatScheme: pricing.vat_scheme,
    vatRateAccommodation: pricing.vat_rate_accommodation,
    pricesIncludeVat: pricing.prices_include_vat,
    childRates: pricing.child_rates,
    childRateScope: pricing.child_rate_scope,
  })

  // Kein hinterlegter Preis: der Vermieter hat seine Preisliste noch nicht gepflegt.
  // Das ist seine Baustelle und nicht die Auskunft, die der Gast bekommen soll.
  if (priced.warnings.some((w) => w.code === 'no_rate')) return null
  if (priced.totals.gross <= 0) return null

  return {
    nights: priced.nightCount,
    gross: priced.totals.gross,
    prepayment: prepaymentFor(priced.totals.gross, prepaymentPolicyOf({
      prepayment_mode: pricing.prepayment.mode,
      prepayment_percent: pricing.prepayment.percent,
      prepayment_fixed: pricing.prepayment.fixed,
    })),
    lines: priced.lines
      .filter((l) => l.gross !== 0)
      .map((l) => ({ label: lineLabel(l, lang), gross: l.gross })),
    agesMissing: children > 0 && !agesComplete && agesMatter(pricing),
  }
}

/**
 * Der niedrigste Nachtpreis, den diese Einheit im kommenden Jahr hat.
 *
 * Steht bei der Auswahl der Einheit — dort, wo der Gast entscheidet, und dort, wo
 * „Maximal 4 Personen" allein nichts entscheidet. Ausdrücklich ein „ab": über den
 * Aufpreis für Zusatzpersonen, die Nebenkosten und die Ortstaxe sagt er nichts, und
 * genau deshalb steht die vollständige Schätzung weiter unten.
 */
export function fromPrice(
  config: EnquiryConfig, unit: EnquiryUnit, from: ISODate = todayISO(),
): number | null {
  const pricing = config.pricing
  if (!pricing) return null
  const engineUnit = toUnit(unit)
  if (!engineUnit) return null
  const rates = toRates(pricing)

  let min: number | null = null
  for (let i = 0; i < 365; i++) {
    const price = resolveNightlyRate(engineUnit, rates, addDays(from, i)).price
    if (price <= 0) continue
    if (min === null || price < min) min = price
  }
  return min
}

/* ────────────────────────────────────────────────────────── die Vorschau ──── */

/**
 * `/e/demo` — die Anfrageseite des Demobetriebs.
 *
 * WARUM ES SIE GIBT: in den Einstellungen der Demo steht der Link auf die eigene
 * Anfrageseite, und ein Interessent klickt ihn an, um zu sehen, was seine Gäste sehen
 * werden. Der Demobetrieb existiert aber nur im Browser — der Server kennt ihn nicht,
 * und die Seite endete mit „Diese Seite ist nicht verfügbar".
 *
 * WARUM SIE SICH DIE DATEN SELBST HOLT: die Anfrageseite läuft vor der Anmeldesperre.
 * Wer den Link weitergibt, öffnet sie in einem Browser, der vom Demomodus nichts weiß.
 * Ein Zugriff auf den Store würde genau dort ins Leere greifen.
 */
export function demoEnquiry(): { config: EnquiryConfig; db: Database } {
  const db = buildDemoDatabase()
  const t = db.tenant
  const units = db.units.filter((u) => u.active)
  const category = (id: string | null | undefined) =>
    db.unit_categories.find((c) => c.id === id)?.name ?? null

  const config: EnquiryConfig = {
    slug: DEMO_SLUG,
    name: t.name,
    country: t.country,
    currency: t.currency,
    locale: t.locale,
    accent: t.accent_color ?? null,
    // Der Demobetrieb hat keinen hinterlegten Einleitungstext; die Vorschau braucht
    // trotzdem einen, sonst zeigt sie eine leere Zeile, wo beim Kunden ein Satz steht.
    intro: {
      de: 'Anfrage unverbindlich — wir melden uns innerhalb eines Tages.',
      en: 'Non-binding enquiry — we reply within a day.',
    },
    lead_days: 1,
    max_nights: null,
    default_open: true,
    units: units.map((u) => ({
      id: u.id, name: u.name, kind: u.kind,
      category: category(u.category_id),
      category_id: u.category_id ?? null,
      max_occupancy: u.max_occupancy, min_nights: u.min_nights,
      bedrooms: u.bedrooms ?? null, size_sqm: u.size_sqm ?? null,
      description: u.description, amenities: u.amenities,
      base_price: u.base_price, base_occupancy: u.base_occupancy,
      extra_person_price: u.extra_person_price,
    })),
    pricing: {
      vat_scheme: t.vat_scheme,
      vat_rate_accommodation: t.vat_rate_accommodation,
      prices_include_vat: t.prices_include_vat ?? false,
      child_rates: t.child_rates ?? [],
      child_rate_scope: t.child_rate_scope ?? 'surcharge',
      prepayment: {
        mode: t.prepayment_mode ?? 'percent',
        percent: t.prepayment_percent ?? 30,
        fixed: t.prepayment_fixed ?? 0,
      },
      rates: db.rate_periods.filter((r) => r.active).map((r) => ({
        id: r.id, unit_id: r.unit_id, category_id: r.category_id ?? null,
        valid_from: r.valid_from, valid_to: r.valid_to, dow_mask: r.dow_mask,
        price_per_night: r.price_per_night, extra_person_price: r.extra_person_price,
        base_occupancy: r.base_occupancy, min_nights: r.min_nights, priority: r.priority,
      })),
      fees: db.fees.filter((f) => f.active && f.mandatory && f.code !== 'tourist_tax'),
      tourist_tax: DEMO_TOURIST_TAX,
    },
  }

  return { config, db }
}

/**
 * Belegte Nächte in der Vorschau, aus denselben Daten und nach denselben Regeln wie
 * `public_enquiry_blocked` auf dem Server: eine Buchung ab „Option" belegt, eine
 * Anfrage nicht, und ein importierter Portaleintrag belegt ebenfalls.
 *
 * Der Demobetrieb hat Buchungen rund um heute. Wer in der Vorschau ein paar Termine
 * durchprobiert, läuft deshalb in ein „nicht alles frei" — und sieht damit genau das,
 * wofür die Verfügbarkeitsprüfung da ist.
 */
export function demoBlockedNights(
  db: Database, unitId: string, from: ISODate, to: ISODate,
): ISODate[] {
  if (!unitId || !from || !to || to <= from) return []
  const occupied = new Set<ISODate>()

  const cover = (arrival: ISODate, departure: ISODate) => {
    for (let d = arrival; d < departure; d = addDays(d, 1)) {
      if (d >= from && d < to) occupied.add(d)
    }
  }

  for (const b of db.bookings) {
    if (b.unit_id !== unitId) continue
    const takes = b.kind === 'block'
      || ['option', 'confirmed', 'checked_in', 'checked_out'].includes(b.status)
    if (takes) cover(b.arrival, b.departure)
  }
  for (const e of db.ical_events) {
    if (e.unit_id !== unitId || e.dismissed) continue
    cover(e.arrival, e.departure)
  }

  return [...occupied].sort()
}
